/**
 * Subscription Context Provider
 * 
 * Manages subscription state with optimistic updates
 * Shows subscriptions as "processing" until confirmed in database
 * 
 * Usage:
 * <SubscriptionProvider>
 *   <App />
 * </SubscriptionProvider>
 * 
 * In components:
 * const { subscriptions, addPendingSubscription, getSubscriptionStatus } = useSubscription();
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  // Actual subscriptions from database
  const [subscriptions, setSubscriptions] = useState([]);
  
  // Plan IDs that are pending confirmation (just added, waiting for webhook)
  const [pendingPlanIds, setPendingPlanIds] = useState([]);
  
  // Timestamp when pending was added (for timeout detection)
  const [pendingTimestamps, setPendingTimestamps] = useState({});

  /**
   * Add plan IDs to pending state (optimistic update)
   * Called immediately after user adds subscription
   */
  const addPendingSubscription = useCallback((planIds) => {
    const ids = Array.isArray(planIds) ? planIds : [planIds];
    const timestamp = Date.now();
    
    setPendingPlanIds(prev => {
      // Only add if not already pending
      const newIds = ids.filter(id => !prev.includes(id));
      return [...prev, ...newIds];
    });
    
    // Track when each plan was added (for timeout)
    setPendingTimestamps(prev => {
      const updates = {};
      ids.forEach(id => {
        if (!prev[id]) {
          updates[id] = timestamp;
        }
      });
      return { ...prev, ...updates };
    });
    
    console.log(`📝 Added pending subscriptions: plan IDs [${ids.join(', ')}]`);
  }, []);

  /**
   * Confirm subscription is in database (remove from pending)
   * Called after polling confirms subscription exists
   */
  const confirmSubscription = useCallback((planId) => {
    setPendingPlanIds(prev => prev.filter(id => id !== planId));
    setPendingTimestamps(prev => {
      const updated = { ...prev };
      delete updated[planId];
      return updated;
    });
    console.log(`✅ Confirmed subscription: plan ID ${planId}`);
  }, []);

  /**
   * Confirm multiple subscriptions at once
   */
  const confirmSubscriptions = useCallback((planIds) => {
    const ids = Array.isArray(planIds) ? planIds : [planIds];
    setPendingPlanIds(prev => prev.filter(id => !ids.includes(id)));
    setPendingTimestamps(prev => {
      const updated = { ...prev };
      ids.forEach(id => delete updated[id]);
      return updated;
    });
    console.log(`✅ Confirmed subscriptions: plan IDs [${ids.join(', ')}]`);
  }, []);

  /**
   * Clear all pending subscriptions (error recovery)
   */
  const clearPendingSubscriptions = useCallback(() => {
    setPendingPlanIds([]);
    setPendingTimestamps({});
    console.log('🗑️ Cleared all pending subscriptions');
  }, []);

  /**
   * Update actual subscriptions from database
   * Called after fetching from API
   */
  const updateSubscriptions = useCallback((newSubscriptions) => {
    setSubscriptions(newSubscriptions || []);
    
    // Auto-confirm any pending subscriptions that now exist in database
    const newPlanIds = (newSubscriptions || []).map(sub => sub.plan?.id).filter(Boolean);
    setPendingPlanIds(prev => {
      const stillPending = prev.filter(id => !newPlanIds.includes(id));
      if (stillPending.length < prev.length) {
        const confirmed = prev.filter(id => newPlanIds.includes(id));
        console.log(`✅ Auto-confirmed subscriptions found in database: plan IDs [${confirmed.join(', ')}]`);
      }
      return stillPending;
    });
  }, []);

  /**
   * Get status of a specific subscription plan
   * Returns: 'active', 'processing', or 'inactive'
   */
  const getSubscriptionStatus = useCallback((planId) => {
    // Check if exists in database
    const exists = subscriptions.some(sub => sub.plan?.id === planId);
    
    // Check if pending
    const pending = pendingPlanIds.includes(planId);
    
    // Check how long it's been pending (for timeout detection)
    const pendingSince = pendingTimestamps[planId];
    const pendingDuration = pendingSince ? Date.now() - pendingSince : 0;
    const timedOut = pendingDuration > 15000; // 15 seconds
    
    return {
      planId,
      exists,
      pending,
      status: exists ? 'active' : pending ? 'processing' : 'inactive',
      pendingDuration,
      timedOut,
    };
  }, [subscriptions, pendingPlanIds, pendingTimestamps]);

  /**
   * Check if ANY subscriptions are pending
   */
  const hasPendingSubscriptions = useMemo(() => {
    return pendingPlanIds.length > 0;
  }, [pendingPlanIds]);

  /**
   * Get all pending plan IDs
   */
  const getPendingPlanIds = useCallback(() => {
    return [...pendingPlanIds];
  }, [pendingPlanIds]);

  /**
   * Check if specific plan is confirmed
   */
  const isConfirmed = useCallback((planId) => {
    return subscriptions.some(sub => sub.plan?.id === planId);
  }, [subscriptions]);

  const value = {
    // State
    subscriptions,
    pendingPlanIds,
    hasPendingSubscriptions,
    
    // Actions
    addPendingSubscription,
    confirmSubscription,
    confirmSubscriptions,
    clearPendingSubscriptions,
    updateSubscriptions,
    
    // Queries
    getSubscriptionStatus,
    getPendingPlanIds,
    isConfirmed,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;

